'use client'

/**
 * AuxPowerInput — the generator / reefer add-on.
 *
 * A switch that adds on-board power fuel (bus generator, trailer reefer) to
 * the estimate, plus a gallons-per-hour override that works like the MPG one.
 * Both are remembered per vehicle. Flipping either after Calculate updates
 * the total instantly — the cost is derived from the finished estimate, so
 * the route isn't re-run.
 *
 * Only rendered for vehicles with an `auxPower` preset (not cars).
 */

import { Check, Pencil, RotateCcw } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { track } from '@/lib/analytics'
import { GPH_MAX, GPH_MIN, IDLE_HOURS_MAX } from '@/lib/generator'
import type { VehicleId } from '@/lib/types'
import { getVehicle } from '@/lib/vehicles'

interface AuxPowerInputProps {
	vehicleId: VehicleId
	on: boolean
	onToggle: (on: boolean) => void
	/** Custom burn rate in gal/hr, or null for the preset. */
	gph: number | null
	onGphChange: (gph: number | null) => void
	/** Parked (non-driving) running hours on this route. */
	idleHours: number
	onIdleChange: (hours: number) => void
}

function parseGph(raw: string): number | null {
	const parsed = Number.parseFloat(raw)
	return Number.isFinite(parsed) && parsed >= GPH_MIN && parsed <= GPH_MAX ? parsed : null
}

export function AuxPowerInput({
	vehicleId,
	on,
	onToggle,
	gph,
	onGphChange,
	idleHours,
	onIdleChange,
}: AuxPowerInputProps) {
	const aux = getVehicle(vehicleId).auxPower
	const switchId = useId()
	const [isEditing, setIsEditing] = useState(false)
	const [inputValue, setInputValue] = useState('')

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on vehicle change only
	useEffect(() => {
		setIsEditing(false)
		setInputValue('')
	}, [vehicleId])

	if (!aux) return null

	const hasCustom = gph !== null
	const rate = gph ?? aux.gph
	const inputValid = parseGph(inputValue) !== null

	const handleToggle = (next: boolean) => {
		if (next) track('EnabledAuxPower', { vehicle: vehicleId, kind: aux.label })
		onToggle(next)
	}

	const handleChange = (raw: string) => {
		setInputValue(raw)
		const parsed = parseGph(raw)
		if (parsed === null) return
		if (gph === null) track('OverrodeGph', { vehicle: vehicleId, gph: parsed })
		onGphChange(parsed)
	}

	const handleReset = () => {
		setIsEditing(false)
		setInputValue('')
		onGphChange(null)
	}

	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between gap-2">
				<label
					htmlFor={switchId}
					className="flex min-w-0 cursor-pointer items-center gap-2 text-sm"
				>
					<Switch id={switchId} checked={on} onCheckedChange={handleToggle} />
					<span className="whitespace-nowrap text-text-secondary">{aux.label}</span>
					<span className="whitespace-nowrap font-mono text-text-primary">{rate} gal/hr</span>
					<span
						className={`hidden whitespace-nowrap text-[10px] uppercase tracking-wider sm:inline ${hasCustom ? 'text-brand' : 'text-text-muted'}`}
					>
						{hasCustom ? 'custom' : 'preset'}
					</span>
				</label>
				{!isEditing && (
					<div className="flex shrink-0 items-center gap-1.5">
						<button
							type="button"
							onClick={() => {
								setInputValue(hasCustom ? String(gph) : '')
								setIsEditing(true)
							}}
							className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-brand/60 hover:text-text-primary"
						>
							<Pencil className="size-3" aria-hidden />
							{hasCustom ? 'Edit' : 'Override'}
						</button>
						{hasCustom && (
							<button
								type="button"
								onClick={handleReset}
								className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-muted transition-colors hover:border-error-fg/60 hover:text-error-fg"
								title={`Reset to preset (${aux.gph} gal/hr)`}
							>
								<RotateCcw className="size-3" aria-hidden />
								Reset
							</button>
						)}
					</div>
				)}
			</div>

			{on && <ParkedHours label={aux.label} value={idleHours} onChange={onIdleChange} />}

			{isEditing && (
				<fieldset className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2.5">
					<legend className="flex w-full items-center justify-between text-sm">
						<span className="font-semibold text-text-primary">{aux.label} burn rate</span>
						<button
							type="button"
							onClick={handleReset}
							className="flex items-center gap-1 text-xs text-text-muted transition-colors hover:text-text-primary"
						>
							<RotateCcw className="size-3" aria-hidden />
							Use preset ({aux.gph})
						</button>
					</legend>
					<div className="mt-2 flex items-center gap-2">
						<Input
							type="number"
							inputMode="decimal"
							min={GPH_MIN}
							max={GPH_MAX}
							step={0.05}
							value={inputValue}
							onChange={(e) => handleChange(e.target.value)}
							placeholder={String(aux.gph)}
							className="flex-1 font-mono"
							aria-label={`${aux.label} gallons per hour`}
						/>
						<Button
							type="button"
							variant="default"
							size="sm"
							onClick={() => setIsEditing(false)}
							disabled={!inputValid}
							title={
								inputValid
									? 'Close editor (value is already saved)'
									: `Enter ${GPH_MIN}–${GPH_MAX} gal/hr`
							}
						>
							<Check className="size-3.5" aria-hidden />
							Done
						</Button>
					</div>
					<p className="mt-1.5 text-[10px] text-text-muted">
						Gallons per hour while driving. Saved automatically as you type.
					</p>
				</fieldset>
			)}
		</div>
	)
}

/**
 * Extra running hours while parked (overnights, show days). Route-level, not
 * saved per vehicle — every tour's schedule is different. Empty = 0.
 */
function ParkedHours({
	label,
	value,
	onChange,
}: {
	label: string
	value: number
	onChange: (hours: number) => void
}) {
	const inputId = useId()
	const [text, setText] = useState(value ? String(value) : '')

	// Follow external changes (share link, reset) without fighting the user's typing.
	useEffect(() => {
		setText((prev) =>
			Number.parseFloat(prev || '0') === value ? prev : value ? String(value) : '',
		)
	}, [value])

	const handleChange = (raw: string) => {
		setText(raw)
		if (raw === '') return onChange(0)
		const parsed = Number.parseFloat(raw)
		if (Number.isFinite(parsed) && parsed >= 0 && parsed <= IDLE_HOURS_MAX) onChange(parsed)
	}

	return (
		<div className="flex items-center justify-between gap-2 pl-10 text-sm">
			<label htmlFor={inputId} className="text-text-secondary">
				+ Parked hours
				<span className="ml-1.5 text-[11px] text-text-muted">{label} running off the road</span>
			</label>
			<Input
				id={inputId}
				type="number"
				inputMode="decimal"
				min={0}
				max={IDLE_HOURS_MAX}
				step={1}
				value={text}
				onChange={(e) => handleChange(e.target.value)}
				placeholder="0"
				className="h-8 w-20 text-right font-mono"
			/>
		</div>
	)
}
